import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { v4 as uuidv4 } from 'uuid';

// ─── Cyborg Avatar SVG ────────────────────────────────────────────────────────
// status: 'idle' | 'listening' | 'thinking' | 'speaking'
const CyborgFace = ({ status, volume = 0 }) => {
  const isListening = status === 'listening';
  const isSpeaking  = status === 'speaking';
  const isThinking  = status === 'thinking';

  // Mouth segments open based on volume (0-1)
  const mouthOpen = isSpeaking ? Math.max(0.1, volume) : 0;

  // Eye scan line position animated
  // eslint-disable-next-line no-unused-vars
  const scanY = useRef(0);

  return (
    <svg
      viewBox="0 0 120 120"
      width="120"
      height="120"
      style={{ filter: `drop-shadow(0 0 ${isListening ? 20 : isSpeaking ? 16 : 10}px ${isListening ? '#00eeff' : '#ff4500'})` }}
    >
      <defs>
        <radialGradient id="faceBg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#1a1a2e" />
          <stop offset="100%" stopColor="#0a0a14" />
        </radialGradient>
        <radialGradient id="eyeGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={isListening ? '#00eeff' : '#ff6a00'} stopOpacity="1" />
          <stop offset="100%" stopColor={isListening ? '#0066ff' : '#ff2200'} stopOpacity="0.3" />
        </radialGradient>
        <filter id="blur3">
          <feGaussianBlur stdDeviation="3" />
        </filter>
        <filter id="blur1">
          <feGaussianBlur stdDeviation="1.5" />
        </filter>
        <clipPath id="faceClip">
          <polygon points="60,4 110,30 110,90 60,116 10,90 10,30" />
        </clipPath>
      </defs>

      {/* ── Outer hex frame glow ── */}
      <polygon
        points="60,2 112,29 112,91 60,118 8,91 8,29"
        fill="none"
        stroke={isListening ? '#00eeff' : '#ff4500'}
        strokeWidth="1.5"
        opacity="0.6"
      />
      {/* pulsing outer ring */}
      <polygon
        points="60,2 112,29 112,91 60,118 8,91 8,29"
        fill="none"
        stroke={isListening ? '#00eeff' : '#ff4500'}
        strokeWidth="3"
        opacity="0.15"
        filter="url(#blur3)"
      >
        <animate
          attributeName="opacity"
          values="0.05;0.25;0.05"
          dur={isListening ? '0.7s' : '2.5s'}
          repeatCount="indefinite"
        />
      </polygon>

      {/* ── Face background ── */}
      <polygon
        points="60,6 108,31 108,89 60,114 12,89 12,31"
        fill="url(#faceBg)"
        clipPath="url(#faceClip)"
      />

      {/* ── Circuit lines ── */}
      {[
        'M12,60 L28,60 L32,52 L40,52',
        'M108,60 L92,60 L88,68 L80,68',
        'M36,30 L36,42 L44,42',
        'M84,30 L84,42 L76,42',
        'M36,90 L36,78 L44,78',
        'M84,90 L84,78 L76,78',
      ].map((d, i) => (
        <path
          key={i}
          d={d}
          fill="none"
          stroke={isListening ? '#00eeff' : '#ff4500'}
          strokeWidth="0.8"
          opacity="0.35"
        />
      ))}

      {/* ── Scan line ── */}
      <clipPath id="hexInner">
        <polygon points="60,6 108,31 108,89 60,114 12,89 12,31" />
      </clipPath>
      <line
        x1="12" y1="60" x2="108" y2="60"
        stroke={isListening ? '#00eeff' : '#ff4500'}
        strokeWidth="0.5"
        opacity="0.2"
        clipPath="url(#hexInner)"
      >
        <animate attributeName="y1" values="12;108;12" dur="4s" repeatCount="indefinite" />
        <animate attributeName="y2" values="12;108;12" dur="4s" repeatCount="indefinite" />
      </line>

      {/* ── Left eye ── */}
      <g>
        {/* glow behind */}
        <rect x="28" y="44" width="22" height="10" rx="5" fill={isListening ? '#00eeff' : '#ff4500'} opacity="0.15" filter="url(#blur3)" />
        {/* eye frame */}
        <rect x="28" y="45" width="22" height="8" rx="4" fill="none" stroke={isListening ? '#00eeff' : '#ff6500'} strokeWidth="1" opacity="0.5" />
        {/* iris bar */}
        <rect
          x="30"
          y={isThinking ? 46 : 46}
          width="18"
          height={isThinking ? 2 : 5}
          rx="2"
          fill={isListening ? '#00eeff' : '#ff4500'}
          opacity="0.9"
        >
          {isThinking && (
            <animate attributeName="x" values="30;38;30" dur="0.8s" repeatCount="indefinite" />
          )}
          <animate
            attributeName="height"
            values={isSpeaking ? `${2 + mouthOpen * 4};${5};${2 + mouthOpen * 4}` : '5;3;5'}
            dur={isSpeaking ? '0.15s' : '3s'}
            repeatCount="indefinite"
          />
        </rect>
      </g>

      {/* ── Right eye ── */}
      <g>
        <rect x="70" y="44" width="22" height="10" rx="5" fill={isListening ? '#00eeff' : '#ff4500'} opacity="0.15" filter="url(#blur3)" />
        <rect x="70" y="45" width="22" height="8" rx="4" fill="none" stroke={isListening ? '#00eeff' : '#ff6500'} strokeWidth="1" opacity="0.5" />
        <rect
          x="72"
          y="46"
          width="18"
          height="5"
          rx="2"
          fill={isListening ? '#00eeff' : '#ff4500'}
          opacity="0.9"
        >
          {isThinking && (
            <animate attributeName="x" values="72;80;72" dur="0.8s" repeatCount="indefinite" />
          )}
          <animate
            attributeName="height"
            values={isSpeaking ? `${2 + mouthOpen * 4};${5};${2 + mouthOpen * 4}` : '5;3;5'}
            dur={isSpeaking ? '0.15s' : '3s'}
            repeatCount="indefinite"
          />
        </rect>
      </g>

      {/* ── Nose bridge ── */}
      <line x1="60" y1="55" x2="60" y2="65" stroke="#ff4500" strokeWidth="0.5" opacity="0.3" />

      {/* ── Mouth ── */}
      <g>
        {/* mouth bg glow */}
        <rect
          x="40" y="70"
          width="40"
          height={isSpeaking ? 8 + mouthOpen * 10 : 6}
          rx="3"
          fill={isListening ? '#00eeff' : '#ff4500'}
          opacity="0.08"
          filter="url(#blur1)"
        />
        {/* outer frame */}
        <rect x="40" y="71" width="40" height="6" rx="3" fill="none" stroke={isListening ? '#00eeff' : '#ff4500'} strokeWidth="0.8" opacity="0.4" />

        {/* mouth bars — 5 vertical segments */}
        {[0, 1, 2, 3, 4].map((i) => {
          const barH = isSpeaking
            ? Math.max(2, (Math.sin(i * 1.2 + mouthOpen * 10) * 0.5 + 0.5) * mouthOpen * 10 + 2)
            : 2;
          const x = 43 + i * 8;
          return (
            <rect
              key={i}
              x={x}
              y={74 - barH / 2}
              width="5"
              height={barH}
              rx="2"
              fill={isListening ? '#00eeff' : '#ff4500'}
              opacity="0.85"
            >
              {isSpeaking && (
                <animate
                  attributeName="height"
                  values={`2;${4 + Math.random() * 6};2`}
                  dur={`${0.1 + i * 0.05}s`}
                  repeatCount="indefinite"
                />
              )}
            </rect>
          );
        })}
      </g>

      {/* ── Listening: ear wave ── */}
      {isListening && (
        <>
          {[1, 2, 3].map((r) => (
            <circle key={r} cx="60" cy="60" r={r * 16} fill="none" stroke="#00eeff" strokeWidth="0.5" opacity={0.3 / r}>
              <animate attributeName="r" values={`${r * 12};${r * 20};${r * 12}`} dur="1.2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values={`${0.3 / r};0;${0.3 / r}`} dur="1.2s" repeatCount="indefinite" />
            </circle>
          ))}
        </>
      )}

      {/* ── Thinking: rotating scan ── */}
      {isThinking && (
        <circle cx="60" cy="60" r="50" fill="none" stroke="#ff4500" strokeWidth="1" strokeDasharray="20 160" opacity="0.4">
          <animateTransform attributeName="transform" type="rotate" values="0 60 60;360 60 60" dur="1s" repeatCount="indefinite" />
        </circle>
      )}

      {/* ── Corner dots ── */}
      {[[20, 25], [100, 25], [20, 95], [100, 95]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2" fill={isListening ? '#00eeff' : '#ff4500'} opacity="0.4">
          <animate attributeName="opacity" values="0.2;0.7;0.2" dur={`${1.5 + i * 0.3}s`} repeatCount="indefinite" />
        </circle>
      ))}
    </svg>
  );
};

// ─── Voice hook ───────────────────────────────────────────────────────────────
const useSpeech = () => {
  const synthRef   = useRef(null);
  const recogRef   = useRef(null);
  const [supported, setSupported] = useState({ synth: false, recog: false });

  useEffect(() => {
    setSupported({
      synth:  'speechSynthesis' in window,
      recog:  'webkitSpeechRecognition' in window || 'SpeechRecognition' in window,
    });
    if ('speechSynthesis' in window) synthRef.current = window.speechSynthesis;
  }, []);

  const speak = useCallback((text, onStart, onEnd, onBoundary) => {
    if (!synthRef.current) return;
    synthRef.current.cancel();
    // Clean markdown
    const clean = text.replace(/[*_`#>•]/g, '').replace(/\n+/g, ' ').trim();
    const utt = new SpeechSynthesisUtterance(clean);
    utt.lang = 'es-AR';
    utt.rate = 0.9;
    utt.pitch = 0.85;
    utt.volume = 1;

    // Pick a voice
    const voices = synthRef.current.getVoices();
    const esVoice = voices.find(v => v.lang.startsWith('es') && v.name.toLowerCase().includes('male'))
                 || voices.find(v => v.lang.startsWith('es'))
                 || voices[0];
    if (esVoice) utt.voice = esVoice;

    utt.onstart   = onStart;
    utt.onend     = onEnd;
    utt.onerror   = onEnd;
    utt.onboundary = onBoundary;
    synthRef.current.speak(utt);
  }, []);

  const stopSpeaking = useCallback(() => {
    synthRef.current?.cancel();
  }, []);

  const startListening = useCallback((onResult, onEnd) => {
    const SR = window.webkitSpeechRecognition || window.SpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.lang = 'es-AR';
    r.interimResults = true;
    r.maxAlternatives = 1;
    r.onresult = (e) => {
      const transcript = Array.from(e.results).map(r => r[0].transcript).join('');
      onResult(transcript, e.results[e.results.length - 1].isFinal);
    };
    r.onend = onEnd;
    r.onerror = onEnd;
    r.start();
    recogRef.current = r;
  }, []);

  const stopListening = useCallback(() => {
    recogRef.current?.stop();
  }, []);

  return { supported, speak, stopSpeaking, startListening, stopListening };
};

// ─── Constants ────────────────────────────────────────────────────────────────
const QUICK_ACTIONS = [
  '💰 ¿Cuánto cuesta una app?',
  '🤖 Contame sobre Akira',
  '🚀 ¿Qué tecnologías usás?',
  '📅 Agendar reunión',
  '🧠 Agentes de IA',
];

const WELCOME = {
  role: 'assistant',
  content: '¡Hola! Soy ARIA, el asistente IA de TinchoDev 🤖\n\nPuedo ayudarte con presupuestos, info técnica, el proyecto Akira o agendar una reunión. Escribí o usá el micrófono para hablar.',
  id: 'welcome',
};

const STATUS_LABEL = {
  idle:      'En línea · Groq AI',
  listening: '🎙 Escuchando...',
  thinking:  '⚙️ Procesando...',
  speaking:  '🔊 Respondiendo...',
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AIChat() {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput]       = useState('');
  const [status, setStatus]     = useState('idle');   // idle|listening|thinking|speaking
  const [volume, setVolume]     = useState(0);
  const [sessionId]             = useState(() => uuidv4());
  const [voiceEnabled, setVoiceEnabled] = useState(true);

  const bottomRef   = useRef(null);
  const volTimer    = useRef(null);
  const { supported, speak, stopSpeaking, startListening, stopListening } = useSpeech();

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, status]);

  // Simulate mouth volume when speaking
  const animateMouth = useCallback(() => {
    let t = 0;
    const tick = () => {
      t += 0.3;
      setVolume(Math.abs(Math.sin(t) * 0.7 + Math.sin(t * 2.3) * 0.3));
      volTimer.current = requestAnimationFrame(tick);
    };
    volTimer.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(volTimer.current);
  }, []);

  // Send message
  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || status !== 'idle') return;
    stopSpeaking();
    setInput('');

    setMessages(prev => [...prev, { role: 'user', content: msg, id: Date.now() }]);
    setStatus('thinking');

    try {
      const { data } = await axios.post(
        'https://back-end-portafolio-swart.vercel.app/api/ai/chat',
        { message: msg, sessionId }
      );
      const reply = data.response;
      setMessages(prev => [...prev, { role: 'assistant', content: reply, id: Date.now() + 1 }]);

      if (voiceEnabled && supported.synth) {
        setStatus('speaking');
        animateMouth();
        speak(
          reply,
          () => {},
          () => {
            cancelAnimationFrame(volTimer.current);
            setVolume(0);
            setStatus('idle');
          },
          null
        );
      } else {
        setStatus('idle');
      }
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '⚠️ No pude conectarme al servidor. Intentá de nuevo.',
        id: Date.now() + 1,
      }]);
      setStatus('idle');
    }
  }, [input, status, sessionId, voiceEnabled, supported, speak, animateMouth, stopSpeaking]);

  // Voice input
  const handleMic = useCallback(() => {
    if (status === 'listening') {
      stopListening();
      setStatus('idle');
      return;
    }
    if (status !== 'idle') return;
    stopSpeaking();
    setStatus('listening');
    setInput('');

    startListening(
      (transcript, isFinal) => {
        setInput(transcript);
        if (isFinal) {
          stopListening();
          send(transcript);
        }
      },
      () => {
        if (status === 'listening') setStatus('idle');
      }
    );
  }, [status, stopListening, stopSpeaking, startListening, send]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const handleClose = () => {
    stopSpeaking();
    stopListening();
    setStatus('idle');
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(o => !o);
    if (open) handleClose();
  };

  // Colors
  const accentColor = status === 'listening' ? '#00eeff' : '#ff4500';
  const isIdle = status === 'idle';

  return (
    <>
      <style>{`
        @keyframes pulse-fab { 0%,100%{box-shadow:0 0 20px rgba(255,69,0,0.5),0 4px 15px rgba(0,0,0,0.4)} 50%{box-shadow:0 0 35px rgba(255,69,0,0.8),0 4px 15px rgba(0,0,0,0.4)} }
        @keyframes pulse-fab-listen { 0%,100%{box-shadow:0 0 20px rgba(0,238,255,0.5)} 50%{box-shadow:0 0 40px rgba(0,238,255,0.9)} }
        @keyframes spin-slow { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes blink-dot { 0%,100%{opacity:1} 50%{opacity:0.3} }
        .ai-fab-btn { animation: pulse-fab 3s ease-in-out infinite; }
        .ai-fab-btn.listening { animation: pulse-fab-listen 0.8s ease-in-out infinite; }
        .ai-messages::-webkit-scrollbar { width: 3px; }
        .ai-messages::-webkit-scrollbar-thumb { background: rgba(255,69,0,0.3); border-radius: 3px; }
        .ai-textarea:focus { border-color: rgba(255,69,0,0.6) !important; outline: none; }
        .ai-textarea::placeholder { color: rgba(255,255,255,0.25); }
        .ai-quick:hover { background: rgba(255,69,0,0.2) !important; color: #fff !important; }
        .ai-send:hover { transform: scale(1.08); box-shadow: 0 0 12px rgba(255,69,0,0.6); }
        .ai-mic:hover { transform: scale(1.08); }
        @keyframes bounce-dot { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }
      `}</style>

      {/* ── FAB ───────────────────────────────────────────────── */}
      <motion.button
        className={`ai-fab-btn${status === 'listening' ? ' listening' : ''}`}
        onClick={handleOpen}
        whileTap={{ scale: 0.9 }}
        style={{
          position: 'fixed', bottom: '2rem', right: '2rem', zIndex: 9999,
          width: '60px', height: '60px', borderRadius: '50%',
          background: status === 'listening'
            ? 'linear-gradient(135deg, #0088aa, #00eeff)'
            : 'linear-gradient(135deg, #ff4500, #ff7b47)',
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.5rem', transition: 'background 0.3s',
        }}
        title="Asistente IA"
      >
        {open ? '✕' : status === 'listening' ? '🎙' : '🤖'}
      </motion.button>

      {/* Badge */}
      <AnimatePresence>
        {!open && (
          <motion.div
            initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
            style={{
              position: 'fixed', bottom: '4.5rem', right: '1.7rem', zIndex: 10000,
              background: '#ff4500', color: 'white', borderRadius: '50%',
              width: '20px', height: '20px', fontSize: '0.6rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, pointerEvents: 'none',
            }}
          >
            IA
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Chat Window ────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.88 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.88 }}
            transition={{ type: 'spring', damping: 22, stiffness: 280 }}
            style={{
              position: 'fixed', bottom: '5.5rem', right: '2rem', zIndex: 9998,
              width: 'min(400px, 96vw)',
              background: 'rgba(10,10,14,0.97)',
              border: `1px solid ${accentColor}44`,
              borderRadius: '20px',
              display: 'flex', flexDirection: 'column',
              boxShadow: `0 0 50px ${accentColor}22, 0 24px 60px rgba(0,0,0,0.9)`,
              backdropFilter: 'blur(24px)',
              overflow: 'hidden',
              maxHeight: '90vh',
            }}
          >

            {/* ── CYBORG AVATAR PANEL ────────────────────────── */}
            <div style={{
              background: `linear-gradient(180deg, rgba(20,12,8,0.98) 0%, rgba(10,10,14,0.98) 100%)`,
              borderBottom: `1px solid ${accentColor}33`,
              padding: '1.25rem 1rem 0.75rem',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              position: 'relative', overflow: 'hidden',
            }}>
              {/* Corner hex decorations */}
              <svg style={{ position: 'absolute', top: 0, left: 0, width: '60px', height: '60px', opacity: 0.2 }} viewBox="0 0 60 60">
                <polyline points="0,30 0,0 30,0" fill="none" stroke={accentColor} strokeWidth="1" />
                <circle cx="8" cy="8" r="2" fill={accentColor} />
              </svg>
              <svg style={{ position: 'absolute', top: 0, right: 0, width: '60px', height: '60px', opacity: 0.2 }} viewBox="0 0 60 60">
                <polyline points="60,30 60,0 30,0" fill="none" stroke={accentColor} strokeWidth="1" />
                <circle cx="52" cy="8" r="2" fill={accentColor} />
              </svg>

              {/* Avatar */}
              <motion.div
                animate={{ scale: status === 'speaking' ? [1, 1.04, 1] : 1 }}
                transition={{ duration: 0.2, repeat: status === 'speaking' ? Infinity : 0 }}
              >
                <CyborgFace status={status} volume={volume} />
              </motion.div>

              {/* Name + status */}
              <div style={{ textAlign: 'center', marginTop: '0.6rem' }}>
                <p style={{ color: '#f2f2f2', fontWeight: 800, fontSize: '0.95rem', margin: 0, letterSpacing: '2px', textTransform: 'uppercase' }}>
                  A·R·I·A
                </p>
                <p style={{ color: accentColor, fontSize: '0.7rem', margin: '0.2rem 0 0', letterSpacing: '1px', transition: 'color 0.3s' }}>
                  <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: status === 'thinking' ? '#ffaa00' : status === 'listening' ? '#00eeff' : '#44ff88', marginRight: '5px', verticalAlign: 'middle', animation: 'blink-dot 1s infinite' }} />
                  {STATUS_LABEL[status]}
                </p>
              </div>

              {/* Voice toggle */}
              {supported.synth && (
                <button
                  onClick={() => { setVoiceEnabled(v => !v); stopSpeaking(); }}
                  title={voiceEnabled ? 'Silenciar voz' : 'Activar voz'}
                  style={{
                    position: 'absolute', top: '0.75rem', right: '0.75rem',
                    background: voiceEnabled ? 'rgba(255,69,0,0.12)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${voiceEnabled ? 'rgba(255,69,0,0.3)' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: '8px', color: voiceEnabled ? '#ff7b47' : 'rgba(242,242,242,0.3)',
                    padding: '0.25rem 0.5rem', fontSize: '0.75rem', cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {voiceEnabled ? '🔊' : '🔇'}
                </button>
              )}
            </div>

            {/* ── MESSAGES ───────────────────────────────────── */}
            <div
              className="ai-messages"
              style={{
                flex: 1, overflowY: 'auto', padding: '0.85rem',
                display: 'flex', flexDirection: 'column', gap: '0.65rem',
                maxHeight: '280px', minHeight: '160px',
                scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,69,0,0.3) transparent',
              }}
            >
              {messages.map(msg => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    maxWidth: '85%',
                    padding: '0.6rem 0.85rem',
                    borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                    background: msg.role === 'user'
                      ? 'linear-gradient(135deg, #ff4500, #ff7b47)'
                      : 'rgba(255,255,255,0.05)',
                    color: '#f2f2f2',
                    fontSize: '0.83rem',
                    lineHeight: '1.55',
                    alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    border: msg.role === 'user' ? 'none' : '1px solid rgba(255,69,0,0.12)',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}
                >
                  {msg.content}
                </motion.div>
              ))}

              {status === 'thinking' && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{
                    display: 'flex', gap: '4px', padding: '0.6rem 0.85rem',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,69,0,0.12)',
                    borderRadius: '14px 14px 14px 4px',
                    alignSelf: 'flex-start', alignItems: 'center',
                  }}
                >
                  {[0, 0.15, 0.3].map((d, i) => (
                    <div key={i} style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ff4500', animation: `bounce-dot 1.2s ${d}s infinite` }} />
                  ))}
                </motion.div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* ── QUICK ACTIONS ─────────────────────────────── */}
            <div style={{ display: 'flex', gap: '0.4rem', padding: '0 0.85rem 0.5rem', overflowX: 'auto', scrollbarWidth: 'none' }}>
              {QUICK_ACTIONS.map(q => (
                <button
                  key={q}
                  className="ai-quick"
                  onClick={() => send(q)}
                  disabled={!isIdle}
                  style={{
                    padding: '0.28rem 0.65rem', borderRadius: '20px', fontSize: '0.7rem',
                    background: 'rgba(255,69,0,0.08)', border: '1px solid rgba(255,69,0,0.25)',
                    color: '#ff7b47', cursor: isIdle ? 'pointer' : 'not-allowed',
                    whiteSpace: 'nowrap', opacity: isIdle ? 1 : 0.5, transition: 'all 0.2s',
                    flexShrink: 0,
                  }}
                >
                  {q}
                </button>
              ))}
            </div>

            {/* ── INPUT ─────────────────────────────────────── */}
            <div style={{
              padding: '0.65rem 0.85rem',
              borderTop: '1px solid rgba(255,69,0,0.12)',
              display: 'flex', gap: '0.45rem', alignItems: 'flex-end',
            }}>
              {/* Mic button */}
              {supported.recog && (
                <motion.button
                  className="ai-mic"
                  onClick={handleMic}
                  whileTap={{ scale: 0.9 }}
                  style={{
                    width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
                    background: status === 'listening'
                      ? 'linear-gradient(135deg, #0088aa, #00eeff)'
                      : 'rgba(255,255,255,0.06)',
                    border: `1px solid ${status === 'listening' ? '#00eeff' : 'rgba(255,255,255,0.1)'}`,
                    color: status === 'listening' ? 'white' : 'rgba(242,242,242,0.5)',
                    cursor: status === 'speaking' || status === 'thinking' ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1rem', transition: 'all 0.2s',
                    opacity: (status === 'speaking' || status === 'thinking') ? 0.4 : 1,
                  }}
                  title={status === 'listening' ? 'Detener escucha' : 'Hablar con ARIA'}
                >
                  🎙
                </motion.button>
              )}

              <textarea
                className="ai-textarea"
                placeholder={status === 'listening' ? 'Escuchando...' : 'Escribí o usá el micrófono...'}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                rows={1}
                disabled={!isIdle}
                style={{
                  flex: 1, background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${status === 'listening' ? 'rgba(0,238,255,0.4)' : 'rgba(255,69,0,0.25)'}`,
                  borderRadius: '10px', color: '#f2f2f2', padding: '0.55rem 0.8rem',
                  fontSize: '0.83rem', resize: 'none', fontFamily: 'inherit',
                  maxHeight: '90px', lineHeight: '1.4', transition: 'border-color 0.3s',
                }}
              />

              {/* Stop speaking */}
              {status === 'speaking' && (
                <motion.button
                  initial={{ scale: 0 }} animate={{ scale: 1 }}
                  onClick={() => { stopSpeaking(); setStatus('idle'); }}
                  style={{
                    width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
                    background: 'rgba(255,69,0,0.15)', border: '1px solid rgba(255,69,0,0.4)',
                    color: '#ff7b47', cursor: 'pointer', fontSize: '1rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                  title="Detener voz"
                >
                  ⏹
                </motion.button>
              )}

              {/* Send */}
              <button
                className="ai-send"
                onClick={() => send()}
                disabled={!isIdle || !input.trim()}
                style={{
                  width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
                  background: 'linear-gradient(135deg, #ff4500, #ff7b47)',
                  border: 'none', cursor: isIdle && input.trim() ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1rem', transition: 'all 0.15s',
                  opacity: isIdle && input.trim() ? 1 : 0.4,
                }}
              >
                🚀
              </button>
            </div>

            {/* Credit */}
            <p style={{ color: 'rgba(242,242,242,0.15)', fontSize: '0.6rem', textAlign: 'center', padding: '0 0 0.5rem', margin: 0 }}>
              ARIA · Powered by Groq LLaMA 3.3 70B
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
